import { Injectable, inject, signal, computed, PLATFORM_ID, OnDestroy } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';

import { InactivityConfig, DEFAULT_INACTIVITY_CONFIG, INACTIVITY_CONFIG } from '../config/inactivity.config';
import { HeaderService } from '../../shared/services/header.service';

import { AuthService } from './auth.service';

export interface InactivityState {
  isActive: boolean;
  showWarning: boolean;
  warningTimeLeft: number;
  isLoggedOut: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class InactivityService implements OnDestroy {
  private readonly STORAGE_KEY = 'session_activity';
  private readonly BROADCAST_CHANNEL = 'session';

  private platformId = inject(PLATFORM_ID);
  private router = inject(Router);
  private authService = inject(AuthService);
  private headerService = inject(HeaderService);
  private readonly config: InactivityConfig =
    inject(INACTIVITY_CONFIG, { optional: true }) ?? DEFAULT_INACTIVITY_CONFIG;

  private inactivityTimer: any = null;
  private warningTimer: any = null;
  private warningCountdownTimer: any = null;
  private broadcastChannel: BroadcastChannel | null = null;

  private _isActive = signal(false);
  private _showWarning = signal(false);
  private _warningTimeLeft = signal(60);
  private _isLoggedOut = signal(false);

  public isActive = computed(() => this._isActive());
  public showWarning = computed(() => this._showWarning());
  public warningTimeLeft = computed(() => this._warningTimeLeft());
  public isLoggedOut = computed(() => this._isLoggedOut());

  private activityEvents: string[];

  constructor() {
    this.activityEvents = this.config.monitoredEvents;
    
    this._warningTimeLeft.set(Math.floor(this.config.warningTimeMs / 1000));

    if (isPlatformBrowser(this.platformId)) {
      if (this.config.enableMultiTabSync) {
        this.initializeBroadcastChannel();
      }
      this.setupActivityListeners();
    }
  }

  startMonitoring(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    this._isActive.set(true);
    this._isLoggedOut.set(false);
    this.resetInactivityTimer();
  }

  stopMonitoring(): void {
    this._isActive.set(false);
    this.clearAllTimers();
    this.removeActivityListeners();
  }

  resetActivity(): void {
    if (!this._isActive()) return;

    this.resetInactivityTimer();
    this.hideWarning();
    this.updateLastActivityTimestamp();
    
    // Notifica outras abas sobre a atividade
    if (this.config.enableMultiTabSync) {
      this.broadcastToOtherTabs({ type: 'activity' });
    }
  }

  continueSession(): void {
    this.hideWarning();
    this.resetActivity();
    
    // Notifica outras abas que a sessão continuou
    if (this.config.enableMultiTabSync) {
      this.broadcastToOtherTabs({ type: 'session_continued' });
    }
  }

  logoutNow(): void {
    this.performLogout();
  }

  dismissLoggedOutModal(): void {
    this._isLoggedOut.set(false);
  }

  private initializeBroadcastChannel(): void {
    if (!this.config.enableMultiTabSync) return;
    
    try {
      this.broadcastChannel = new BroadcastChannel(this.BROADCAST_CHANNEL);
      this.broadcastChannel.addEventListener('message', (event) => {
        this.handleBroadcastMessage(event.data);
      });
    } catch (error) {
      console.warn('BroadcastChannel não suportado, usando fallback com localStorage');
      this.setupStorageEventListener();
    }
  }

  private setupStorageEventListener(): void {
    window.addEventListener('storage', (event) => {
      if (event.key === this.STORAGE_KEY && event.newValue) {
        const data = JSON.parse(event.newValue);
        this.handleBroadcastMessage(data);
      }
    });
  }

  private handleBroadcastMessage(data: any): void {
    switch (data.type) {
      case 'activity':
        this.resetActivity();
        break;
      case 'session_continued':
        this.hideWarning();
        this.resetInactivityTimer();
        break;
      case 'warning_shown':

        // Se outra aba já está mostrando o warning, não mostra nesta
        if (!this._showWarning()) {
          this.clearAllTimers();
        }
        break;
      case 'logout':
        this.handleLogoutFromOtherTab();
        break;
    }
  }

  private broadcastToOtherTabs(message: any): void {
    if (!this.config.enableMultiTabSync) return;
    
    if (this.broadcastChannel) {
      this.broadcastChannel.postMessage(message);
    } else {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify({
        ...message,
        timestamp: Date.now()
      }));
      
      setTimeout(() => localStorage.removeItem(this.STORAGE_KEY), 100);
    }
  }

  private setupActivityListeners(): void {
    this.activityEvents.forEach(event => {
      document.addEventListener(event, this.onActivity.bind(this), true);
    });
  }

  private removeActivityListeners(): void {
    this.activityEvents.forEach(event => {
      document.removeEventListener(event, this.onActivity.bind(this), true);
    });
  }

  private onActivity(): void {
    if (this._showWarning()) return;
    this.resetActivity();
  }

  private resetInactivityTimer(): void {
    this.clearAllTimers();
    
    this.inactivityTimer = setTimeout(() => {
      this.showInactivityWarning();
    }, this.config.inactivityTimeMs);
  }

  private showInactivityWarning(): void {
    // Verifica se já existe um warning ativo em outra aba
    if (this.isWarningActiveInOtherTab()) {
      return;
    }

    this._showWarning.set(true);
    this._warningTimeLeft.set(Math.floor(this.config.warningTimeMs / 1000));
    
    // Notifica outras abas que o warning está sendo mostrado
    if (this.config.enableMultiTabSync) {
      this.broadcastToOtherTabs({ type: 'warning_shown' });
    }

    this.startWarningCountdown();
    
    this.warningTimer = setTimeout(() => {
      this.performLogout();
    }, this.config.warningTimeMs);
  }

  private startWarningCountdown(): void {
    this.warningCountdownTimer = setInterval(() => {
      const timeLeft = this._warningTimeLeft() - 1;
      this._warningTimeLeft.set(timeLeft);
      
      if (timeLeft <= 0) {
        clearInterval(this.warningCountdownTimer);
      }
    }, 1000);
  }

  private hideWarning(): void {
    this._showWarning.set(false);
    this.clearWarningTimers();
  }

  private performLogout(): void {
    this.clearAllTimers();
    this._showWarning.set(false);
    this._isActive.set(false);
    this._isLoggedOut.set(true);

    // Notifica outras abas sobre o logout
    if (this.config.enableMultiTabSync) {
      this.broadcastToOtherTabs({ type: 'logout' });
    }

    // Realiza o logout através do AuthService
    this.authService.logout();
    
    // Reset da aba ativa para Hub de Soluções
    this.headerService.resetActiveTopNavItem();
    
    this.router.navigate(['/']);
  }

  private handleLogoutFromOtherTab(): void {
    this.clearAllTimers();
    this._showWarning.set(false);
    this._isActive.set(false);
    this._isLoggedOut.set(true);
    
    // Reset da aba ativa para Hub de Soluções
    this.headerService.resetActiveTopNavItem();
    
    this.router.navigate(['/']);
  }

  private clearAllTimers(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
    this.clearWarningTimers();
  }

  private clearWarningTimers(): void {
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
    if (this.warningCountdownTimer) {
      clearInterval(this.warningCountdownTimer);
      this.warningCountdownTimer = null;
    }
  }

  private isWarningActiveInOtherTab(): boolean {
    // Verifica se há um warning ativo recente (últimos 5 segundos)
    const lastWarning = localStorage.getItem('warning_timestamp');
    if (lastWarning) {
      const warningTime = parseInt(lastWarning);
      const now = Date.now();
      return (now - warningTime) < 5000; // 5 segundos de tolerância
    }
    return false;
  }

  private updateLastActivityTimestamp(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    
    try {
      const storageKey = 'umkt_login_data';
      const savedData = localStorage.getItem(storageKey);
      if (savedData) {
        const loginData = JSON.parse(savedData);
        loginData.lastActivity = Date.now();
        localStorage.setItem(storageKey, JSON.stringify(loginData));
      }
    } catch (error) {
      console.error('Erro ao atualizar timestamp da última atividade:', error);
    }
  }

  ngOnDestroy(): void {
    this.stopMonitoring();
    if (this.broadcastChannel) {
      this.broadcastChannel.close();
    }
  }
}
